import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { Users, ClipboardList, ShieldCheck, Loader2, CheckCircle2, ArrowRight } from "lucide-react";

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland",
  "Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington",
  "West Virginia","Wisconsin","Wyoming","Other / International",
];

const FADE = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function TheBlackCensus() {
  const [counts, setCounts] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", year_of_birth: "", gender: "", ethnicity_note: "",
    place_of_birth: "", residence_city: "", residence_state: "", household_size: "", heritage: "", consent: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/census/count");
      setCounts(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error loading census counts:", err);
      }
    }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim() || !form.residence_state) {
      setError("Please provide your full name and state of residence."); return;
    }
    if (!form.consent) {
      setError("Please consent to be recorded on the Census rolls."); return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, household_size: form.household_size ? parseInt(form.household_size, 10) : null };
      const { data } = await api.post("/census", payload);
      setDone(data);
      loadCounts();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="tribal-stripe h-1.5 w-full" />

      {/* Hero + live counter */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16 lg:py-20">
        <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">An Autonomous Count of Our People</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[0.95] text-[#F5F3EF]">
          The Black Census
        </h1>
        <p className="mt-7 text-lg text-[#A8A29E] leading-relaxed max-w-3xl">
          Be counted on the autonomous <span className="text-[#F5F3EF] font-medium">Indigenous Black American Census rolls</span> —
          a record kept <span className="text-[#D4AF37]">by our people, for our people</span>. You do not have to become a
          member to be counted. Recording yourself here is a simple act of presence: a declaration that you exist, that you
          are here, and that you wish to be numbered among the Nation's people.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15 mt-12">
          <CounterCard icon={ClipboardList} label="Recorded on the Census" value={counts?.census_count} accent />
          <CounterCard icon={Users} label="Enrolled Members" value={counts?.member_count} />
          <CounterCard icon={Users} label="Souls in Recorded Households" value={counts?.household_souls} />
        </div>
      </section>

      {/* Confirmation OR Form */}
      <section className="max-w-3xl mx-auto px-6 sm:px-12 pb-24">
        {done ? (
          <motion.div {...FADE} className="bg-[#141414] border border-[#5DBB7A]/40 p-10 sm:p-14 text-center" data-testid="census-confirmation">
            <CheckCircle2 className="w-14 h-14 text-[#5DBB7A] mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-serif-display text-4xl text-[#F5F3EF]">You Have Been Counted</h2>
            <p className="text-[#A8A29E] mt-4 max-w-md mx-auto">
              Thank you, <span className="text-[#F5F3EF]">{done.full_name}</span>. You are now recorded on the autonomous
              Indigenous Black American Census rolls for <span className="text-[#F5F3EF]">{done.residence_state}</span>.
            </p>
            <div className="mt-8 inline-block border border-[#D4AF37]/40 px-8 py-5">
              <div className="text-xs uppercase tracking-[0.2em] text-[#A8A29E]">Census Record No.</div>
              <div className="font-mono-doc text-3xl text-[#D4AF37] mt-1">{done.census_id}</div>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link to="/register" data-testid="census-to-register">
                <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-8 py-6">
                  Ready to Become a Member? <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Button variant="outline" onClick={() => { setDone(null); setForm({ full_name: "", email: "", year_of_birth: "", gender: "", ethnicity_note: "", place_of_birth: "", residence_city: "", residence_state: "", household_size: "", heritage: "", consent: false }); }}
                className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8 py-6" data-testid="census-record-another">
                Record Another Person
              </Button>
            </div>
          </motion.div>
        ) : (
          <div className="bg-[#141414] border border-[#D4AF37]/30 p-8 sm:p-12">
            <h2 className="font-serif-display text-3xl text-[#F5F3EF]">Record Yourself on the Rolls</h2>
            <p className="text-[#A8A29E] mt-2 mb-8 text-sm">Only your name and state of residence are required. Everything else helps us understand our people. No account needed.</p>

            {error && (
              <div className="bg-[#C8102E]/15 border border-[#C8102E]/40 text-[#F5A3A3] px-4 py-3 mb-6 text-sm" data-testid="census-error">{error}</div>
            )}

            <form onSubmit={submit} className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label="Full Name *" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} testid="census-fullname" />
                <Field label="Email (optional)" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} testid="census-email" />
                <Field label="Year of Birth (optional)" placeholder="e.g. 1985" value={form.year_of_birth} onChange={(e) => set("year_of_birth", e.target.value)} testid="census-yob" />
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">Gender (optional)</Label>
                  <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger className="mt-2 rounded-none border-0 border-b-2 border-[#F5F3EF]/20 bg-transparent text-[#F5F3EF] focus:ring-0 px-0 text-lg" data-testid="census-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {["Female","Male","Non-binary","Prefer not to say"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Location of Birth (optional)" placeholder="City, State / Country" value={form.place_of_birth} onChange={(e) => set("place_of_birth", e.target.value)} testid="census-pob" />
                <Field label="City of Residence (optional)" value={form.residence_city} onChange={(e) => set("residence_city", e.target.value)} testid="census-city" />
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">State of Residence *</Label>
                  <Select value={form.residence_state} onValueChange={(v) => set("residence_state", v)}>
                    <SelectTrigger className="mt-2 rounded-none border-0 border-b-2 border-[#F5F3EF]/20 bg-transparent text-[#F5F3EF] focus:ring-0 px-0 text-lg" data-testid="census-state"><SelectValue placeholder="Select your state" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Household Size (optional)" type="number" placeholder="People in your household" value={form.household_size} onChange={(e) => set("household_size", e.target.value)} testid="census-household" />
              </div>

              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">Ancestry / Heritage Notes (optional)</Label>
                <Textarea value={form.heritage} onChange={(e) => set("heritage", e.target.value)} placeholder="Anything you wish to record about your lineage, family origins, or community..."
                  className="mt-2 rounded-none border-2 border-[#F5F3EF]/15 bg-transparent text-[#F5F3EF] focus-visible:ring-0 focus-visible:border-[#D4AF37]" data-testid="census-heritage" />
              </div>

              <label className="flex items-start gap-3 cursor-pointer border-t border-[#F5F3EF]/10 pt-6">
                <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-1 rounded-none border-[#F5F3EF]/40 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]" data-testid="census-consent" />
                <span className="text-sm text-[#A8A29E]">I consent to be recorded on the autonomous Indigenous Black American Census rolls. I understand this is a community record and does not by itself alter my citizenship or legal status, and is separate from formal membership.</span>
              </label>

              <Button type="submit" disabled={submitting} className="w-full rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white py-6 text-base" data-testid="census-submit-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Count Me on the Census"}
              </Button>
            </form>
          </div>
        )}

        <div className="flex items-start gap-3 mt-8 text-xs text-[#A8A29E]/70 leading-relaxed">
          <ShieldCheck className="w-5 h-5 text-[#5DBB7A] shrink-0 mt-0.5" />
          <p>Your information is held by the Nation as a private community record. The Census is voluntary and is offered for those who wish to be counted without yet joining as members. When you are ready, you can <Link to="/register" className="text-[#D4AF37] hover:underline">become a full member</Link> at any time.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CounterCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-8 ${accent ? "bg-[#141414]" : "bg-[#0A0A0A]"}`}>
      <Icon className="w-6 h-6 text-[#D4AF37] mb-4" strokeWidth={1.5} />
      <div className="font-serif-display text-5xl font-semibold text-[#F5F3EF]">
        {value == null ? "—" : value.toLocaleString()}
      </div>
      <div className="text-xs uppercase tracking-wider mt-1 text-[#A8A29E]">{label}</div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">{label}</Label>
      <Input {...props} data-testid={testid}
        className="mt-2 rounded-none border-0 border-b-2 border-[#F5F3EF]/20 bg-transparent text-[#F5F3EF] focus-visible:ring-0 focus-visible:border-[#D4AF37] px-0 text-lg" />
    </div>
  );
}
